import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Empty,
  Input,
  Modal,
  Radio,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  message, Badge, Dropdown, Menu, Popconfirm,
} from "antd";
import {
  AppstoreOutlined, BorderInnerOutlined,
  CopyOutlined, DeleteOutlined,
  DownloadOutlined, DownOutlined,
  EditOutlined,
  EyeOutlined,
  FilePdfOutlined, FilePptOutlined, FileSyncOutlined, FolderAddOutlined, GlobalOutlined, HistoryOutlined, PictureOutlined,
  ReloadOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import calendar from "dayjs/plugin/calendar";

import { presentationsApi, templatesApi } from "../../../api/presentationsApi";
import {
  IPresentationTemplate,
  ITemplateDetail,
  ITemplateRow,
  NAMESPACE_LABELS,
  STATE_COLORS,
  STATE_LABELS,
} from "../../../types/models/presentations";
import RecordChannels from "../../RecordChannels";
import FloorPlanWizard from "../floorPlans/FloorPlanWizard";
import MapImageModal from "../maps/MapImageModal";
import GalleryPicksModal from "../gallery/GalleryPicksModal";
import AmenityPhotosModal from "../gallery/AmenityPhotosModal";
import PrepareModal from "./PrepareModal";
import TeaserSlidesModal from "./TeaserSlidesModal";

// Lazy: the listing wizard brings the app's whole model graph with it, and
// only the click on a property name needs it.
const ListingEditWizard = lazy(() => import("../ListingEditWizard"));

/**
 * The row's open checklist steps, as the table can know them cheaply.
 *
 * Copy, floor plans and the amenity grid are counted on the server for the
 * whole page (`readiness.todo_for_listings`); gallery photos, the map and the
 * property's details need a payload each, so only the Prepare panel's full
 * checklist sees them — which is why Generate always asks for it first rather
 * than trusting an empty list here.
 */
export const todoOf = (row: ITemplateRow) => ({
  blocking: row.todo?.blocking ?? ((row.copy?.blocking ?? 0) > 0 ? ["Copy"] : []),
  review: row.todo?.review ?? ((row.copy?.drafts ?? 0) > 0 ? ["Copy"] : []),
});

/**
 * Everything behind one template, in one place.
 *
 * This replaces the coverage matrix, which hardcoded four namespace/language
 * cells and could show only a status — no dates, no files, no way to act. The
 * template is what people recognise, so the instances belong under it.
 *
 * The list deliberately includes properties with *no* output yet. "This template
 * covers 14 properties, 6 have a brochure" is the useful sentence, and a list of
 * finished files cannot say it.
 */

const Body = styled.div`
  .summary {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }

  .headline {
    font-size: 22px;
    font-weight: 600;
    line-height: 1.2;
  }

  .sub {
    color: rgba(0, 0, 0, 0.45);
    font-size: 13px;
  }

  .destination {
    display: block;
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  .row-error {
    color: #cf1322;
    font-size: 12px;
  }

  .orphan {
    opacity: 0.7;
  }

  .filters {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 12px;
  }

  .files {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
  }

  .pptx {
    color: rgba(0, 0, 0, 0.4);
    font-size: 12px;
  }

  .pptx:hover {
    color: rgba(0, 0, 0, 0.75);
  }
`;

// Same as everywhere else in the admin: "Today at 14:32" beats "3 hours ago"
// when the question is whether a file predates this morning's listing edit.
const day: any = dayjs.extend(calendar);

/** Statuses that mean work is in flight, so the drawer should keep polling. */
const IN_FLIGHT = ["pending"];

/**
 * Measured on a real 18-slide, 35 MB brochure: fill, upload, LibreOffice, upload
 * again, about 55 seconds. The engine holds a semaphore of one, so they do not
 * overlap — which is exactly why the number matters before someone presses the
 * button on a hundred properties.
 */
const SECONDS_EACH = 55;

function roughDuration(count: number) {
  const minutes = Math.round((count * SECONDS_EACH) / 60);
  if (minutes < 2) return "under a minute";
  if (minutes < 60) return `about ${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `about ${hours}h${rest ? ` ${rest}m` : ""}`;
}

/**
 * Accent- and case-insensitive, because most of the portfolio is Spanish:
 * "Merida" has to find Mérida, and "avandaro" has to find Bosque Avándaro.
 * Anyone typing a query is doing it to avoid scrolling, and a search that
 * demands the right diacritic is slower than scrolling.
 */
function normalize(value: string | null | undefined) {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function searchMatches(row: ITemplateRow, query: string) {
  if (!query) return true;
  const needle = normalize(query).trim();
  return (
    normalize(row.listing.name).includes(needle) ||
    normalize(row.listing.destination).includes(needle)
  );
}

type Filter = "all" | "rendered" | "missing" | "attention";

const NEEDS_ATTENTION = ["stale", "failed", "needs_info"];

function matches(row: ITemplateRow, filter: Filter) {
  const status = row.presentation?.status;
  switch (filter) {
    case "rendered":
      return Boolean(row.presentation?.pptx_url);
    case "missing":
      return row.in_audience && !row.presentation?.pptx_url;
    case "attention":
      return Boolean(status && NEEDS_ATTENTION.includes(status));
    default:
      return true;
  }
}

function whenRendered(row: ITemplateRow) {
  const at = row.presentation?.generated_at;
  if (!at) return <span className="sub">—</span>;
  return (
    <Tooltip title={dayjs(at).format("D MMM YYYY, HH:mm")}>
      <span style={{ fontSize: 12, opacity: 0.75 }}>{day(at).calendar()}</span>
    </Tooltip>
  );
}

function TemplateDrawer({
  template,
  onClose,
  onChanged,
  onPreview,
  onEdit,
  onViewPdf,
  initialQuery,
}: {
  template: IPresentationTemplate | null;
  /** Open already searched for this — the monitor opens one property's row. */
  initialQuery?: string;
  onClose: () => void;
  onChanged: () => void;
  onPreview: (template: IPresentationTemplate) => void;
  onEdit: (template: IPresentationTemplate) => void;
  /** Hands a finished brochure to the same viewer the template preview uses. */
  onViewPdf: (title: string, url: string, downloadUrl?: string | null,
              onEdit?: () => void) => void;
}) {
  const [detail, setDetail] = useState<ITemplateDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  /** Unpublished properties are hidden unless asked for. */
  const [showUnpublished, setShowUnpublished] = useState(false);
  const [page, setPage] = useState(1);
  /** The row whose Prepare panel is open. */
  const [prepare, setPrepare] = useState<ITemplateRow>();
  /** Bumped when the edit wizard closes, so an open Prepare panel re-checks. */
  const [prepareKey, setPrepareKey] = useState(0);
  const [choosingTeaser, setChoosingTeaser] = useState(false);

  /** Listing ids with a single-row generate in flight, so the button can spin. */
  const [running, setRunning] = useState<number[]>([]);
  // The listing whose edit wizard is open, by slug.
  const [editing, setEditing] = useState<string | null>(null);
  const poll = useRef<number>();

  const templateId = template ? Number(template.id) : null;

  const load = useCallback(
    async (quiet = false) => {
      if (templateId === null) return;
      if (!quiet) setLoading(true);
      try {
        setDetail(await templatesApi.detail(templateId));
      } catch (e) {
        if (!quiet) message.error("Could not load this template.");
      } finally {
        setLoading(false);
      }
    },
    [templateId]
  );

  useEffect(() => {
    if (templateId === null) {
      setDetail(null);
      return;
    }
    setQuery(initialQuery ?? "");
    setFilter("all");
    // The monitor opens one property's row, which may not be published.
    setShowUnpublished(Boolean(initialQuery));
    load();
  }, [templateId, initialQuery, load]);

  // Without this, narrowing 109 rows to 3 while on page 3 shows an empty table.
  useEffect(() => {
    setPage(1);
  }, [query, filter, showUnpublished]);

  // Generation runs on a worker and takes tens of seconds, so the drawer refreshes
  // itself while anything is in flight and stops the moment nothing is.
  useEffect(() => {
    const working = detail?.rows.some(
      (row) => row.presentation && (IN_FLIGHT.includes(row.presentation.status)
        || (row.presentation.teaser && IN_FLIGHT.includes(row.presentation.teaser.status)))
    );
    if (!working) {
      window.clearTimeout(poll.current);
      return;
    }
    poll.current = window.setTimeout(() => load(true), 3000);
    return () => window.clearTimeout(poll.current);
  }, [detail, load]);

  const runGenerate = async () => {
    if (templateId === null) return;
    setQueueing(true);
    try {
      const { queued, blocked } = await templatesApi.generateMissing(templateId);
      const skipped = blocked.length
        ? ` ${blocked.length} not ready, skipped — open Prepare on each.`
        : "";
      if (queued === 0 && !blocked.length) {
        message.success("Everything is already up to date.");
      } else if (queued === 0) {
        message.warning(`Nothing queued.${skipped}`);
      } else {
        message.success(
          `Queued ${queued} ${queued === 1 ? "property" : "properties"}.${skipped}`);
      }
      load(true);
      onChanged();
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? "Could not queue those.");
    } finally {
      setQueueing(false);
    }
  };

  // Renders are serialised and take about a minute each, so the count and the
  // time are shown before anything is queued rather than discovered afterwards.
  const generateMissing = () => {
    if (pending === 0) {
      message.info("Everything is already up to date.");
      return;
    }
    if (pending < 5) {
      runGenerate();
      return;
    }
    // Deliberately a count rather than the per-row prompt: a modal per listing
    // across a whole audience is unusable, and the bulk action is the one place
    // where being told "3 of 14" is more useful than being stopped.
    // Said before anything is queued, not discovered one job at a time: the
    // server skips these rows too, and this explains why.
    const blocked = rows.filter((r) => r.in_audience
      && !["ready", "pinned", "pending"].includes(r.presentation?.status ?? "")
      && todoOf(r).blocking.length > 0);
    const reasons = new Map<string, number>();
    blocked.forEach((r) => todoOf(r).blocking.forEach(
      (label) => reasons.set(label, (reasons.get(label) ?? 0) + 1)));
    const going = pending - blocked.length;
    Modal.confirm({
      title: `Generate ${going} presentations?`,
      content: `They run one at a time on the engine, so this takes ${roughDuration(
        going
      )}. Customized presentations are left alone.`
        + (blocked.length
          ? ` ${blocked.length} are not ready and will be skipped (`
            + Array.from(reasons.entries())
              .map(([label, count]) => `${count} on ${label.toLowerCase()}`).join(", ")
            + "). Open Prepare on each to see what they need."
          : ""),
      okText: "Generate them",
      onOk: runGenerate,
    });
  };

  const generateOne = async (row: ITemplateRow) => {
    if (!template) return;
    const listingId = row.listing.id;
    setRunning((ids) => [...ids, listingId]);
    try {
      await presentationsApi.generate(
        listingId,
        template.namespace,
        template.language
      );
      load(true);
      onChanged();
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? "Could not queue that one.");
    } finally {
      setRunning((ids) => ids.filter((id) => id !== listingId));
    }
  };

  /**
   * Generate, or say first what is missing.
   *
   * Always asks the server's full checklist, not the table's cheap summary:
   * the summary cannot see a gallery short of photos or a map with no
   * coordinates, and either would fail on the engine a minute later. Anything
   * blocking opens the Prepare panel instead, with a button per fix.
   */
  const rerender = async (row: ITemplateRow) => {
    if (!template) return;
    const listingId = row.listing.id;
    setRunning((ids) => [...ids, listingId]);
    let blocked = false;
    try {
      blocked = (await templatesApi.checklist(Number(template.id), listingId)).blocked;
    } catch (e: any) {
      message.error("Could not check this property before generating.");
      setRunning((ids) => ids.filter((id) => id !== listingId));
      return;
    }
    setRunning((ids) => ids.filter((id) => id !== listingId));
    if (blocked) {
      setPrepare(row);
      return;
    }
    // Re-rendering a customized presentation throws away the hand edits that
    // made it customized, so that one asks first.
    if (row.presentation?.status === "pinned") {
      Modal.confirm({
        title: "Replace the customized version?",
        content:
          `${row.listing.name} has a customized presentation. Re-rendering ` +
          "builds it again from current listing data and the edits made outside " +
          "the system will be gone.",
        okText: "Re-render anyway",
        okButtonProps: { danger: true },
        onOk: () => generateOne(row),
      });
      return;
    }
    generateOne(row);
  };

  const search = (value: string) => {
    setQuery(value);
    setFilter("all");
  };

  const rows = detail?.rows ?? [];
  const rendered = rows.filter(
    (row) => row.in_audience && row.presentation?.pptx_url
  ).length;
  const orphans = rows.filter((row) => !row.in_audience).length;
  const covers = detail?.audience_count ?? 0;
  // The detail's copy, which is current after the teaser is changed here.
  const current = detail?.template ?? template;
  const teaserSlides = current?.teaser_slide_ids ?? [];
  // What "Generate missing" would actually queue: the backend skips customized
  // and in-flight rows, so this counts the same way.
  const pending = rows.filter(
    (row) =>
      row.in_audience &&
      !["ready", "pinned", "pending"].includes(row.presentation?.status ?? "")
  ).length;
  const unpublished = rows.filter((row) => !row.listing.published).length;
  // The status filters count only what can be shown, so "Missing 4" lists four.
  const shown = showUnpublished ? rows : rows.filter((row) => row.listing.published);
  const shownRendered = shown.filter((row) => matches(row, "rendered")).length;
  const shownMissing = shown.filter((row) => matches(row, "missing")).length;
  const shownAttention = shown.filter((row) => matches(row, "attention")).length;
  const inFilter = shown.filter((row) => matches(row, filter));
  const visible = inFilter.filter((row) => searchMatches(row, query));

  const columns = [
    {
      title: "Property",
      key: "listing",
      render: (_: any, row: ITemplateRow) => (
        <div className={row.in_audience ? "" : "orphan"}>
          <a onClick={() => setEditing(row.listing.slug)}>
            {row.listing.name}
          </a>
          <span className="destination">{row.listing.destination ?? "—"}</span>
        </div>
      ),
      width: 185
    },
    {
      // What each property still needs before it can be generated, from the
      // cheap whole-page counts. Clicking opens the full checklist, which is
      // also where every fix lives — before the first generation as well as
      // after it.
      title: "Prepare",
      key: "prepare",
      width: 120,
      render: (_: any, row: ITemplateRow) => {
        const { blocking, review } = todoOf(row);
        const cell = blocking.length > 0 ? (
          <Tooltip title={`Still needed: ${blocking.join(", ")}`}>
            <Tag color="error">{blocking.length} to do</Tag>
          </Tooltip>
        ) : review.length > 0 ? (
          <Tooltip title={`Not reviewed yet: ${review.join(", ")}. It will generate.`}>
            <Tag color="blue">Review</Tag>
          </Tooltip>
        ) : (
          <Tooltip title="Nothing missing that the table can see. Open to run the full check.">
            <span className="sub">Check</span>
          </Tooltip>
        );
        return <a onClick={() => setPrepare(row)}>{cell}</a>;
      },
    },
    {
      title: "Status",
      key: "status",
      width: 180,
      render: (_: any, row: ITemplateRow) => {
        const presentation = row.presentation;

        if (!row.in_audience) {
          return (
            <Tooltip title="This property now uses a more specific template. The file below is the one made from this template.">
              <Tag>No longer covered</Tag>
            </Tooltip>
          );
        }
        const state = row.presentation?.status ?? "not_generated";
        return (
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
            <Tooltip title={row.presentation?.error || ""}>
              <Space direction="vertical" size={0}>
              <Badge status={STATE_COLORS[state] as any} text={STATE_LABELS[state]} />
              {state === "ready" && whenRendered(row)}
              </Space>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "",
      key: "generated_at",
      width: 140,
      render: (_: any, row: ITemplateRow) => {
        const presentation = row.presentation;
        const busy = running.includes(row.listing.id) || presentation?.status === "pending";
        if (busy) {
          return (
            <span className="sub">
              <Spin size="small" /> Generating…
            </span>
          );
        }

        // Nothing rendered yet: the whole cell is the way to start one.
        if (!presentation?.pptx_url) {
          return (
            <Button
              size="small"
              icon={<ThunderboltOutlined />}
              disabled={!row.in_audience || !template?.is_active}
              onClick={() => rerender(row)}
            >
              Generate
            </Button>
          );
        }

        return (
          <Space size={4}>
            <Tooltip title="Render it again from current listing data">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                disabled={!row.in_audience || !template?.is_active}
                onClick={() => rerender(row)}
              >
                Re-gen
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      // The PDF is the deliverable. The .pptx is for the occasional "I need to
      // tweak one thing in Canva", so it is deliberately quiet rather than an
      // equal sibling — a row with two identical icon buttons makes the choice
      // look like a coin flip.
      title: "",
      key: "files",
      // width: 150,
      // align: "right" as const,
      render: (_: any, row: ITemplateRow) => {
        const presentation = row.presentation;
        if (!presentation?.pptx_url) return null;
        return (
          <div className="row-actions">
            {presentation.pdf_url ? (
              <Tooltip title="preview PDF file">
              <a
                type="link"
                //size="small"
                onClick={() =>
                  onViewPdf(
                    row.listing.name,
                    presentation.pdf_url as string,
                    presentation.pptx_url,
                    () => setPrepare(row)
                  )
                }
              >
                <FilePdfOutlined style={{ fontSize: 22 }} />
              </a>
              </Tooltip>
            ) : (
              <Tooltip title="This one was built without a PDF. Re-render it to get one.">
                <Button size="small" icon={<FilePdfOutlined />} disabled>
                  PDF
                </Button>
              </Tooltip>
            )}
            <Tooltip title="download PPTX file">
              <a href={presentation.pptx_url}>
                <FilePptOutlined style={{ fontSize: 22 }} />
              </a>
            </Tooltip>
            {presentation.teaser && (
              presentation.teaser.pdf_url ? (
                <Tooltip title="preview the teaser PDF">
                  <a onClick={() => onViewPdf(
                    `${row.listing.name} — teaser`,
                    presentation.teaser!.pdf_url as string,
                    presentation.teaser!.pptx_url,
                  )}>
                    Teaser
                  </a>
                </Tooltip>
              ) : (
                <Tooltip title={presentation.teaser.error || presentation.teaser.status_label}>
                  <Tag color={presentation.teaser.status === "failed" ? "red" : undefined}
                       style={{ marginRight: 0 }}>
                    Teaser {presentation.teaser.status === "failed" ? "failed" : "…"}
                  </Tag>
                </Tooltip>
              )
            )}

            <Dropdown
              overlay={(
                <Menu>
                  <Menu.Item disabled={!row.listing.design}>
                    {row.listing.design ? (
                      <FloorPlanWizard
                        designId={row.listing.design}
                        unitId={row.listing.unit ?? undefined}
                        recordName={row.listing.name}
                      >
                        <a><BorderInnerOutlined style={{ fontSize: 16, marginRight: 4 }} /> Floor Plan</a>
                      </FloorPlanWizard>
                    ) : (
                      <Tooltip title="This property has no unit, so there is no design to hold a floor plan.">
                        <span><BorderInnerOutlined style={{ fontSize: 16, marginRight: 4 }} /> Floor Plan</span>
                      </Tooltip>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    <MapImageModal
                      listingId={row.listing.id}
                      listingName={row.listing.name}
                    >
                      <a><GlobalOutlined style={{ fontSize: 16, marginRight: 4 }} /> Map Image</a>
                    </MapImageModal>
                  </Menu.Item>
                  <Menu.Item>
                    <GalleryPicksModal
                      listingId={row.listing.id}
                      listingName={row.listing.name}
                      listingSlug={row.listing.slug}
                    >
                      <a><PictureOutlined style={{ fontSize: 16, marginRight: 4 }} /> Gallery Photos</a>
                    </GalleryPicksModal>
                  </Menu.Item>
                  <Menu.Item>
                    <AmenityPhotosModal
                      listingId={row.listing.id}
                      listingName={row.listing.name}
                      listingSlug={row.listing.slug}
                    >
                      <a><AppstoreOutlined style={{ fontSize: 16, marginRight: 4 }} /> Amenity Photos</a>
                    </AmenityPhotosModal>
                  </Menu.Item>
                </Menu>
              )}
            >
              <a>Adjust <DownOutlined /></a>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <Drawer
      visible={template !== null}
      onClose={onClose}
      width={820}
      title={template?.name}
      destroyOnClose
    >
      {prepare && template && (
        <PrepareModal
          template={template}
          row={rows.find((r) => r.listing.id === prepare.listing.id) ?? prepare}
          open
          refreshKey={prepareKey}
          onClose={() => { setPrepare(undefined); load(true); }}
          onGenerate={generateOne}
          onEditListing={setEditing}
          onChanged={() => load(true)}
        />
      )}
      {editing && (
        <Suspense fallback={null}>
          <ListingEditWizard
            slug={editing}
            onClose={() => {
              setEditing(null);
              setPrepareKey((key) => key + 1);
              // A saved edit can change the name, the copy owed and staleness.
              load(true);
            }}
          />
        </Suspense>
      )}
      {template && (
        <Body>
          <div className="summary">
            <div>
              <div className="headline">
                {covers} {covers === 1 ? "property" : "properties"}
                <span className="sub">
                  {" · "}
                  {rendered} rendered
                </span>
              </div>
              <div className="sub">
                {NAMESPACE_LABELS[template.namespace] ?? template.namespace}
                {" · "}
                {template.language.toUpperCase()}
                {" · "}
                {template.slide_count} slides · {template.token_count} tokens
                {template.validated_at && (
                  <> · checked {dayjs(template.validated_at).format("D MMM")}</>
                )}
              </div>
            </div>
            <Space>
              <Tooltip title="Edit name, targeting and status">
                <Button
                  icon={<EditOutlined />}
                  onClick={() => onEdit(template)}
                />
              </Tooltip>
              <Button
                icon={<EyeOutlined />}
                onClick={() => onPreview(template)}
              >
                Preview
              </Button>
              <Tooltip title="The slides a teaser keeps; every brochure is cut down to them">
                <Button onClick={() => setChoosingTeaser(true)}>
                  {teaserSlides.length
                    ? `Teaser · ${teaserSlides.length} slides`
                    : "Teaser"}
                </Button>
              </Tooltip>
              <Tooltip title="Download the template .pptx">
                <Button
                  icon={<DownloadOutlined />}
                  href={template.file_url ?? undefined}
                  disabled={!template.file_url}
                />
              </Tooltip>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => load()}
                loading={loading}
              />
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                loading={queueing}
                disabled={!template.is_active || pending === 0}
                onClick={generateMissing}
              >
                Generate missing{pending > 0 ? ` (${pending})` : ""}
              </Button>
            </Space>
          </div>

          {!template.is_active && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="This template is turned off, so it covers no properties right now."
              description={
                orphans > 0
                  ? `${orphans} file${orphans === 1 ? "" : "s"} made from it earlier are listed below.`
                  : undefined
              }
            />
          )}

          {loading && !detail ? (
            <div style={{ padding: 60, textAlign: "center" }}>
              <Spin />
            </div>
          ) : rows.length === 0 ? (
            <Empty
              description={
                template.is_active
                  ? "No properties resolve to this template yet."
                  : "Nothing has ever been made from this template."
              }
            />
          ) : (
            <>
              <div className="filters">
                <Input
                  allowClear
                  size="small"
                  prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.3)" }} />}
                  placeholder="Property or destination"
                  value={query}
                  onChange={(event) => search(event.target.value)}
                  style={{ width: 240 }}
                />

                <Space size={12}>
                  {unpublished > 0 && (
                    <Checkbox
                      checked={showUnpublished}
                      onChange={(event) => setShowUnpublished(event.target.checked)}
                    >
                      <span className="sub">Show unpublished ({unpublished})</span>
                    </Checkbox>
                  )}
                  <Radio.Group
                    size="small"
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                  >
                    <Radio.Button value="all">All {shown.length}</Radio.Button>
                    <Radio.Button value="rendered">Rendered {shownRendered}</Radio.Button>
                    <Radio.Button value="missing">Missing {shownMissing}</Radio.Button>
                    {shownAttention > 0 && (
                      <Radio.Button value="attention">
                        Needs attention {shownAttention}
                      </Radio.Button>
                    )}
                  </Radio.Group>
                </Space>
              </div>

              {query !== "" && (
                <div className="sub" style={{ marginBottom: 8 }}>
                  {visible.length} of {inFilter.length} shown
                </div>
              )}

              <Table
                rowKey={(row: ITemplateRow) => row.listing.id}
                dataSource={visible}
                columns={columns}
                size="small"
                locale={{
                  emptyText: query
                    ? `Nothing matches “${query}”.`
                    : "Nothing here.",
                }}
                pagination={
                  visible.length > 25
                    ? {
                        current: page,
                        onChange: setPage,
                        pageSize: 25,
                        showSizeChanger: false,
                        size: "small",
                      }
                    : false
                }
              />
            </>
          )}
        </Body>
      )}
      {choosingTeaser && current && (
        <TeaserSlidesModal
          template={current}
          onClose={() => setChoosingTeaser(false)}
          onSaved={() => {
            load(true);
            onChanged();
          }}
        />
      )}
    </Drawer>
  );
}

export default TemplateDrawer;
